import { FC, useEffect, useMemo, useRef, useState } from "react";
import { gql } from "@apollo/client";
import { useRouter } from "next/router";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import ContentAvatar from "@src/features/contentLibrary/ContentAvatar";
import { useTimeslotZone } from "@src/features/formCompletion/useLocationTimeslots";
import {
  Ff,
  LibraryItemType,
  CompletionScheduleInstanceStatus,
} from "@src/types.generated";
import { Button } from "@src/lib/ui/Button";
import { Skeleton } from "@src/lib/ui/Skeleton";
import Text from "@src/lib/ui/Text";
import useIntersectionObserver from "@src/lib/hooks/useIntersectionObserver";
import { cn } from "@src/lib/utils/cn";
import ArrowRightIcon from "@src/lib/icons/18px/arrow-right";
import TriangleWarningIcon from "@src/lib/icons/18px/triangle-warning-fill";
import CircleDottedIcon from "@src/lib/icons/18px/circle-dotted";
import CircleHalfDottedClockIcon from "@src/lib/icons/18px/circle-half-dotted-clock";
import CalendarIcon from "@src/lib/icons/18px/calendar-outline";
import PinIcon from "@src/lib/icons/18px/pin-outline";
import { HomeCarousel } from "./HomeCarousel";
import { HomeSectionCountBadge, HomeSectionTitle } from "./HomeSection";
import { homeFormsTitle, homeFormTiming, orderHomeForms } from "./homeForms";
import {
  HomeForms_InstanceFragment,
  useHomeForms_InstancesQuery,
  useHomeForms_FindAuditDraftMutation,
  useHomeForms_FindChecklistDraftMutation,
  useHomeForms_MyLocationsQuery,
} from "./HomeFormsCarousel.generated";

const REFRESH_MS = 60_000;

const HomeFormsCarousel: FC = () => {
  const enabled = useFeatureFlag(Ff.WebFormCompletion);
  const perWindowDrafts = useFeatureFlag(Ff.ChecklistDraftsPerWindow);
  const checklists = useFeatureFlag(Ff.TaskLibraryItemType);
  const audits = useFeatureFlag(Ff.Audits);
  // Without per-window drafts, the backend ignores the selected occurrence.
  // Keep the loader unmounted so no tile can open or label a generic draft.
  return enabled && perWindowDrafts && (checklists || audits) ? (
    <HomeFormsForMyLocations checklists={!!checklists} audits={!!audits} />
  ) : null;
};

// Home lists only forms at the viewer's own locations. The backend scopes
// non-admins to theirs but returns every location to admins, and ignores an
// empty location filter, so a viewer with no locations gets no row.
const HomeFormsForMyLocations: FC<{ checklists: boolean; audits: boolean }> = (
  props,
) => {
  const { data } = useHomeForms_MyLocationsQuery();
  const locationIds = data?.Me?.user.locations.map((location) => location.id);
  return locationIds?.length ? (
    <HomeFormsLoader {...props} locationIds={locationIds} />
  ) : null;
};

const HomeFormsLoader: FC<{
  checklists: boolean;
  audits: boolean;
  locationIds: number[];
}> = ({ checklists, audits, locationIds }) => {
  const lateSubmissions = useFeatureFlag(Ff.LateChecklistSubmissions) === true;
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), REFRESH_MS);
    return () => clearInterval(timer);
  }, []);
  const checklistQuery = useHomeForms_InstancesQuery({
    variables: {
      input: {
        filters: {
          completed: { value: false },
          libraryItemTypes: { value: [LibraryItemType.Task] },
          locationIds: { value: locationIds },
          ...(lateSubmissions && { includePastDue: { value: true } }),
        },
      },
    },
    skip: !checklists,
    fetchPolicy: "cache-and-network",
    pollInterval: REFRESH_MS,
  });
  const auditQuery = useHomeForms_InstancesQuery({
    variables: {
      input: {
        filters: {
          completed: { value: false },
          libraryItemTypes: { value: [LibraryItemType.Audit] },
          locationIds: { value: locationIds },
        },
      },
    },
    skip: !audits,
    fetchPolicy: "cache-and-network",
    pollInterval: REFRESH_MS,
  });
  const items = useMemo(
    () =>
      orderHomeForms(
        [
          ...(checklists
            ? checklistQuery.data?.CompletionScheduleInstances.objects ?? []
            : []),
          ...(audits
            ? auditQuery.data?.CompletionScheduleInstances.objects ?? []
            : []),
        ],
        lateSubmissions,
      ),
    [checklists, audits, checklistQuery.data, auditQuery.data, lateSubmissions],
  );
  const failedQueries = [
    ...(checklists && checklistQuery.error ? [checklistQuery] : []),
    ...(audits && auditQuery.error ? [auditQuery] : []),
  ];
  if (!items.length && !failedQueries.length) return null;
  return (
    <>
      {failedQueries.length > 0 && (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-2xl border border-border-primary bg-bg-primary p-3"
        >
          <Text variant="fine-print-description">
            Couldn't load{" "}
            {checklists && checklistQuery.error
              ? audits && auditQuery.error
                ? "checklists and audits"
                : "checklists"
              : "audits"}
            .
          </Text>
          <Button
            variant="outline"
            size="sm"
            disabled={failedQueries.some((result) => result.loading)}
            onClick={() => {
              void Promise.allSettled(
                failedQueries.map((result) => result.refetch()),
              );
            }}
          >
            Retry
          </Button>
        </div>
      )}
      {items.length > 0 && <HomeFormsRow items={items} now={now} />}
    </>
  );
};

export const HomeFormsRow: FC<{
  items: HomeForms_InstanceFragment[];
  now: number;
}> = ({ items, now }) => (
  <section
    className="flex min-w-0 flex-col gap-4"
    data-testid="home-forms"
    aria-label={homeFormsTitle(items)}
  >
    <div className="flex items-center gap-2">
      <HomeSectionTitle title={homeFormsTitle(items)} />
      <HomeSectionCountBadge count={items.length} />
    </div>
    <HomeCarousel>
      {items.map((item) => (
        <HomeFormTile key={item.id} item={item} now={now} />
      ))}
    </HomeCarousel>
  </section>
);

const HomeFormTile: FC<{ item: HomeForms_InstanceFragment; now: number }> = ({
  item,
  now,
}) => {
  const router = useRouter();
  const ref = useRef<HTMLDivElement>(null);
  const { isInViewport } = useIntersectionObserver({
    target: ref,
    threshold: 0,
    rootMargin: 260,
  });
  const libraryItem = item.window.completionSchedule.libraryItem;
  const audit = libraryItem.type === LibraryItemType.Audit;
  const [findAudit] = useHomeForms_FindAuditDraftMutation();
  const [findChecklist] = useHomeForms_FindChecklistDraftMutation();
  const [draft, setDraft] = useState<{
    started: boolean;
    error: boolean;
    unavailable?: boolean;
  }>();
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!isInViewport) return;
    if (!libraryItem.skill) {
      setDraft({ started: false, error: true });
      return;
    }
    let cancelled = false;
    const input = {
      skillId: libraryItem.skill.id,
      locationId: item.location.id,
      completionScheduleInstanceId: item.id,
      createIfMissing: false,
    };
    const read = async () => {
      try {
        const result = audit
          ? (await findAudit({ variables: { input } })).data
              ?.getOrCreateAuditInstanceDraft
          : (await findChecklist({ variables: { input } })).data
              ?.getOrCreateTaskListInstanceDraft;
        // Later upcoming windows are visible in Tasks, but cannot be started yet.
        const unavailable =
          item.status === CompletionScheduleInstanceStatus.Upcoming &&
          result?.error?.code === "TIMESLOT_CLOSED";
        if (!cancelled)
          setDraft({
            started: !!result?.skillInstance?.startedAt,
            error: !result?.success && !unavailable,
            unavailable,
          });
      } catch {
        if (!cancelled) setDraft({ started: false, error: true });
      }
    };
    void read();
    return () => {
      cancelled = true;
    };
  }, [
    audit,
    findAudit,
    findChecklist,
    isInViewport,
    item.id,
    item.location.id,
    item.status,
    libraryItem.skill,
    now,
    retry,
  ]);
  const zone = useTimeslotZone(item.location.address?.timezoneStr);
  const timing = homeFormTiming(item, zone, now);
  const status = draft?.error
    ? "Status unavailable"
    : draft?.started
      ? "In progress"
      : "Not started";
  const early =
    item.status === CompletionScheduleInstanceStatus.Upcoming &&
    !!draft &&
    !draft.unavailable;
  const action = draft?.error
    ? "Retry"
    : draft?.started
      ? "Continue"
      : early
        ? "Start early"
        : "Start";
  return (
    <div ref={ref} className="w-65 shrink-0 snap-start">
      <button
        type="button"
        disabled={!draft || draft.unavailable}
        onClick={() => {
          if (draft?.error) {
            setRetry((value) => value + 1);
            return;
          }
          void router.push({
            pathname: "/forms/[id]/complete",
            query: {
              id: libraryItem.id,
              locationId: String(item.location.id),
              timeslotId: item.id,
            },
          });
        }}
        className="flex h-full min-h-45 w-full flex-col gap-3 rounded-2xl border border-border-primary bg-bg-primary p-3 text-left hover:bg-bg-subtle focus-visible:ring-2 focus-visible:ring-state-focus focus-visible:outline-none"
        data-testid="home-form-tile"
      >
        <div className="flex items-start gap-2">
          <ContentAvatar
            size={32}
            contentType={audit ? "Audit" : "Task"}
            coverImage={libraryItem.coverImage}
            defaultBackgroundHashKey={libraryItem.id}
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <Text
              variant="fine-print-description"
              className="text-text-tertiary"
            >
              {audit ? "Audit" : "Checklist"}
            </Text>
            {/* Reserve two lines so the details line up across tiles. */}
            <Text
              variant="compact-title"
              multiline
              className="line-clamp-2 min-h-10"
            >
              {libraryItem.name.myTranslation}
            </Text>
          </div>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-1 text-text-tertiary">
            <PinIcon className="size-3 shrink-0" />
            <Text variant="fine-print-description" className="truncate">
              {item.location.name}
            </Text>
          </div>
          <div
            className={cn(
              "flex items-center gap-1",
              timing.tone === "pastDue"
                ? "text-status-warning-text"
                : timing.tone === "timeRemaining"
                  ? "text-status-success-text"
                  : "text-text-secondary",
            )}
          >
            {timing.tone === "pastDue" ? (
              <TriangleWarningIcon className="size-3 shrink-0 text-status-warning-solid" />
            ) : timing.tone === "timeRemaining" ? (
              <CircleHalfDottedClockIcon className="size-3 shrink-0" />
            ) : (
              <CalendarIcon className="size-3 shrink-0" />
            )}
            <Text variant="fine-print-description">{timing.label}</Text>
          </div>
        </div>
        <div className="mt-auto flex items-center justify-between gap-2 border-t border-border-primary pt-3">
          {!draft ? (
            <Skeleton className="h-5 w-22" />
          ) : (
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5",
                draft.started
                  ? "bg-tag-purple/10 text-tag-purple"
                  : "bg-bg-tertiary text-text-tertiary",
              )}
            >
              {draft.started ? (
                <CircleHalfDottedClockIcon className="size-3" />
              ) : (
                <CircleDottedIcon className="size-3" />
              )}
              <Text variant="micro-title">{status}</Text>
            </span>
          )}
          <Button
            asChild
            variant="outline"
            size="sm"
            className={cn(draft?.unavailable && "opacity-50")}
          >
            <span>
              {action}
              <ArrowRightIcon data-icon="inline-end" />
            </span>
          </Button>
        </div>
      </button>
    </div>
  );
};

export default HomeFormsCarousel;

gql`
  query HomeForms_MyLocations {
    Me {
      user {
        id
        locations {
          id
        }
      }
    }
  }
  query HomeForms_Instances($input: CompletionScheduleInstancesInput!) {
    CompletionScheduleInstances(input: $input) {
      objects {
        ...HomeForms_Instance
      }
    }
  }
  fragment HomeForms_Instance on CompletionScheduleInstance {
    id
    start
    end
    isOpen
    status
    lateDeadline
    completedBySkillInstance {
      id
    }
    location {
      id
      name
      address {
        id
        timezoneStr
      }
    }
    window {
      id
      completionSchedule {
        id
        libraryItem {
          id
          type
          name {
            uuid
            myTranslation
          }
          coverImage {
            ...CoverImage
          }
          skill {
            id
          }
        }
      }
    }
  }
  mutation HomeForms_FindChecklistDraft(
    $input: GetOrCreateTaskListInstanceDraftInput!
  ) {
    getOrCreateTaskListInstanceDraft(input: $input) {
      success
      error {
        code
      }
      skillInstance {
        id
        startedAt
      }
    }
  }
  mutation HomeForms_FindAuditDraft(
    $input: GetOrCreateAuditInstanceDraftInput!
  ) {
    getOrCreateAuditInstanceDraft(input: $input) {
      success
      error {
        code
      }
      skillInstance {
        id
        startedAt
      }
    }
  }
`;
